import { useCallback, useState } from "react";
import {
  type NestUseCase,
  type UploadProgress,
  UploadStatus,
} from "src/api/interfaces/nest";
import type { NestTestResult } from "src/api/interfaces/nest/history";
import { useNestUseCaseFetcher } from "./useNestUseCaseFetcher";
import { useDocumentTypePreference } from "./useDocumentTypePreference";
import client from "src/api/client";
import { useCountrySelection } from "src/components/Contexts/CountrySelectionContext";
import { useNest } from "src/components/Contexts/NestContext";

interface NestContentState {
  selectedUploadUseCase: NestUseCase | null;
  uploadFiles: File[];
  uploadUseCaseId: string;
  uploadDocumentTypeId: string;
  completedUploads: UploadProgress[];
  useCaseHistory: NestUseCase | null;
  uploadResults: Map<string, NestTestResult>;
  selectedUploadTest: NestTestResult | null;
}

const INITIAL_STATE: NestContentState = {
  selectedUploadUseCase: null,
  uploadFiles: [],
  uploadUseCaseId: "",
  uploadDocumentTypeId: "",
  completedUploads: [],
  useCaseHistory: null,
  uploadResults: new Map(),
  selectedUploadTest: null,
};

export function useNestContent() {
  const [state, setState] = useState<NestContentState>(INITIAL_STATE);
  const {
    data: useCases,
    isLoading,
    silentRefetch: refetchUseCases,
  } = useNestUseCaseFetcher();
  const { getDocumentType, setDocumentType } = useDocumentTypePreference();
  const { selectedCountry } = useCountrySelection();
  const { partnerCode } = useNest();

  const resetState = useCallback(
    (overrides?: Partial<NestContentState>) =>
      setState((prev) => ({
        ...INITIAL_STATE,
        uploadResults: prev.uploadResults,
        ...overrides,
      })),
    []
  );

  const handleUpload = (useCase: NestUseCase, documentTypeId: string) => {
    setDocumentType(useCase.id, documentTypeId);
    resetState({
      uploadFiles: state.uploadFiles,
      selectedUploadUseCase: useCase,
      uploadDocumentTypeId: documentTypeId,
    });
  };

  const onNewTest = (useCaseId: string) => {
    const useCase = useCases.find((u) => u.id === useCaseId)!;
    resetState({
      selectedUploadUseCase: useCase,
      uploadDocumentTypeId: getDocumentType(useCase).id,
    });
  };

  const handleFilesSelected = (files: File[], useCaseId: string) => {
    resetState({
      uploadFiles: files,
      uploadUseCaseId: useCaseId,
      uploadDocumentTypeId: state.uploadDocumentTypeId,
      useCaseHistory: state.useCaseHistory,
      selectedUploadTest: state.selectedUploadTest,
    });
  };

  const handleUploadComplete = (results: UploadProgress[]) => {
    const nextResults = new Map(state.uploadResults);
    for (const r of results) {
      if (r.testResult) nextResults.set(r.fileName, r.testResult);
    }

    const singleSuccess = results.length === 1 && results[0].testResult;

    resetState({
      completedUploads: results,
      uploadUseCaseId: state.uploadUseCaseId,
      uploadDocumentTypeId: state.uploadDocumentTypeId,
      uploadResults: nextResults,
      useCaseHistory: state.useCaseHistory,
      selectedUploadTest: singleSuccess
        ? results[0].testResult!
        : state.selectedUploadTest,
    });

    if (singleSuccess) {
      setTimeout(
        () => setState((prev) => ({ ...prev, completedUploads: [] })),
        2000
      );
    }

    refetchUseCases();
  };

  const handleRetry = (fileName?: string) => {
    const toRetry = fileName
      ? state.completedUploads.filter((u) => u.fileName === fileName)
      : state.completedUploads.filter(
          (u) =>
            u.status === UploadStatus.ERROR &&
            u.statusCode &&
            u.statusCode >= 500
        );

    resetState({
      uploadFiles: toRetry.map((u) => u.file),
      uploadUseCaseId: state.uploadUseCaseId,
      uploadDocumentTypeId: state.uploadDocumentTypeId,
      useCaseHistory: state.useCaseHistory,
      selectedUploadTest: state.selectedUploadTest,
    });
  };

  const handleSuccessClick = (fileName: string) => {
    const testResult = state.uploadResults.get(fileName);
    if (testResult) {
      setState((prev) => ({ ...prev, selectedUploadTest: testResult }));
    }
  };

  const handleStatusClick = async (useCase: NestUseCase) => {
    const response = await client.getNestHistory({
      useCaseId: useCase.id,
      runId: useCase.lastRun.id,
      countryCode: selectedCountry?.countryCode,
      partnerCode: partnerCode ?? undefined,
      limit: 1,
    });
    const test = response.tests[0];
    if (test) {
      resetState({ selectedUploadTest: test });
    }
  };

  const handleHistory = (useCase: NestUseCase) => {
    resetState({ uploadFiles: state.uploadFiles, useCaseHistory: useCase });
  };

  return {
    useCases,
    isLoading,
    ...state,
    handleUpload,
    onNewTest,
    handleFilesSelected,
    handleUploadComplete,
    handleRetry,
    handleSuccessClick,
    handleStatusClick,
    handleHistory,
    resetState,
  };
}
